export interface FilterData {
    /** Array with filter blocks */
    filterBlocks: FilterBlock[];
}

export type FilterBlock =
    | CheckboxFilterBlock
    | DropdownFilterBlock
    | SelectFilterBlock;

export enum FilterBlockTypesEnum {
    Checkbox = "checkbox",
    Dropdown = "dropdown",
    Select = "select",
}

export interface BaseFilterBlock {
    /* controls if the filter block is disabled */
    disabled?: boolean;
    /** Array with filter items */
    filterItems?: FilterItem[];
    /** Array with select options */
    options?: FilterOption[];
    /** placeholder for the filter block if used without translations */
    placeholder?: string;
    /** name of filter block, used for input elements and translations */
    name?: string;
    /** type of the filter block */
    type: FilterBlockTypesEnum;
}

export interface CheckboxFilterBlock extends BaseFilterBlock {
    /** filterBlock type `"checkbox"` */
    type: FilterBlockTypesEnum.Checkbox;
    /** checkbox items */
    filterItems: FilterItem[];
}

export interface DropdownFilterBlock extends BaseFilterBlock {
    /** filterBlock type `"dropdown"` */
    type: FilterBlockTypesEnum.Dropdown;
    /** dropdown checkbox items */
    filterItems: FilterItem[];
    /** controls if the dropdown is filterable (via a search input) */
    filterable?: boolean;
}

export interface SelectFilterBlock extends BaseFilterBlock {
    /** filterBlock type `"select"` */
    type: FilterBlockTypesEnum.Select;
    /** controls if a dropdown with checkboxes is displayed instead of a native select element */
    multiple?: boolean;
    /** name of the filter, used as input name attribute and for translations */
    name: string;
    /** filter options (checkboxes or select options) */
    options: FilterOption[];
}

export interface FilterOption {
    /* controls if the option is disabled */
    disabled?: boolean;
    /* optional label of the option if used without translations */
    label?: string;
    /* indicates if the filter item is selected */
    selected?: boolean;
    /* value of the option */
    value: string;
    /* number of filtered items for this filter option */
    count?: number;
}

export interface FilterItem {
    /* name of the filter item, used to assign the input name attribute */
    name?: string;
    /* value of the filter item */
    value: string;
    /* optional label of the filter item if used without translations */
    label?: string;
    /* indicates if the filter item is selected */
    active?: boolean;
    /* controls if the filter item is disabled */
    disabled?: boolean;
    /* number of filtered items for this filter item */
    count?: number;
}

export interface FilterDataProviderState {
    /** all filters */
    readonly data: FilterData | null;
    /** endpoint for fetching filters */
    readonly endpoint: string;
    /** optional error response */
    readonly error: FilterApiError | null;
    /** Flag that indicates if a request is pending */
    readonly pending: boolean;
    /** current filter data with selection based on `filterQuery` */
    readonly filterData: FilterData | null;
    /** Filter query string */
    readonly filterQuery: string;
    /** checks if user has filtered */
    readonly filterQuerySet: boolean;
}

export interface FilterDataProvider extends FilterDataProviderState {
    /** Get data asyncronously */
    getData(): Promise<FilterData | null>;
    /** Set api endpoint url */
    setEndpoint(endpoint: string): void;
    /** Set filter query string */
    setFilterQuery(query: string): void;
    /** The current state */
    readonly state: FilterDataProviderState;
    /** Subscribe to state changes (returns unsubscribe callback) */
    readonly subscribe: (handler: FilterDataProviderCallback) => () => void;
    /** Unsubscribe a  */
    readonly unsubscribe: (handler: FilterDataProviderCallback) => void;
}

export interface FilterDataProviderCallback {
    /** Handler for state changes */
    (state: FilterDataProviderState): void;
}

export interface FilterResponse extends Response {
    /** Overrides the the native Response interface */
    json: () => Promise<FilterData>;
}

export interface FilterApiError {
    /** Status code of the response */
    status: number;
    /** Error message */
    message: string;
}
